import React, { useEffect, useState } from "react";
import { Check, Loader2, RefreshCw, Unlink, Users, Wand2, X } from "lucide-react";
import { toast } from "sonner";
import { Modal, reviewsApi, inputCls, btnCls } from "./shared";

export default function AliasesModal({ open, onClose, onChanged }) {
  const [groups, setGroups] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [dismissed, setDismissed] = useState([]);
  const [loading, setLoading] = useState(false);
  const [raw, setRaw] = useState("");
  const [canon, setCanon] = useState("");

  const load = async () => {
    setLoading(true);
    try { setGroups((await reviewsApi.aliases()).groups || []); } catch {}
    try { setSuggestions((await reviewsApi.aliasSuggestions()).suggestions || []); } catch {}
    setLoading(false);
  };
  useEffect(() => { if (open) { setDismissed([]); load(); } }, [open]);

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); await load(); onChanged?.(); } catch (e) { toast.error(e?.response?.data?.detail || "İşlem başarısız"); } };
  const visibleSuggestions = suggestions.filter(s => !dismissed.includes(s.from_name));

  return (
    <Modal open={open} onClose={onClose} title="Garson İsim Birleştirmeleri" width="max-w-3xl" testId="aliases-modal">
      <p className="text-xs text-slate-400 mb-3">Çift veya hatalı yazılmış garson adları (örn. "FTİH BASMACI", "FATIH BASMACI") otomatik olarak Garsonlar listesindeki asıl ada bağlanır. Yanlış birleşmeyi "Ayır" ile kaldırabilir, aşağıdan elle bağlayabilirsiniz.</p>

      {visibleSuggestions.length > 0 && (
        <div className="mb-4 border border-amber-500/40 bg-amber-500/5 rounded-lg p-3" data-testid="alias-suggestions">
          <div className="text-sm font-semibold text-amber-300 flex items-center gap-2 mb-2"><Wand2 className="w-4 h-4" /> Önerilen Birleştirmeler <span className="text-[11px] font-normal text-slate-400">({visibleSuggestions.length}) — benzer ama birleşmemiş isimler, onaylayın veya yok sayın</span></div>
          <div className="space-y-1.5">
            {visibleSuggestions.map(s => (
              <div key={s.from_name} className="flex items-center justify-between gap-2 bg-slate-800/60 rounded-md px-2.5 py-1.5" data-testid={`alias-suggestion-${s.from_name}`}>
                <div className="text-xs text-slate-200 flex items-center gap-1.5 flex-wrap">
                  <span className="text-rose-200">{s.from_name}</span><span className="text-slate-500">×{s.from_count}</span>
                  <span className="text-slate-500">→</span>
                  <span className="text-emerald-300 font-medium">{s.to_name}</span><span className="text-slate-500">×{s.to_count}</span>
                  <span className="text-[10px] text-amber-400/80 ml-1">%{Math.round(s.score * 100)} benzer</span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button type="button" onClick={() => act(() => reviewsApi.setAlias(s.from_name, s.to_name), "Birleştirildi")} className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white" data-testid={`alias-suggestion-accept-${s.from_name}`}><Check className="w-3 h-3" /> Birleştir</button>
                  <button type="button" onClick={() => setDismissed(d => [...d, s.from_name])} className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-slate-700 hover:bg-slate-600 text-slate-300" title="Yok say" data-testid={`alias-suggestion-dismiss-${s.from_name}`}><X className="w-3 h-3" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <form onSubmit={e => { e.preventDefault(); if (!raw.trim()) return; act(() => reviewsApi.setAlias(raw.trim(), canon.trim()), "Bağlandı"); setRaw(""); setCanon(""); }} className="flex flex-wrap gap-2 mb-3">
        <input value={raw} onChange={e => setRaw(e.target.value)} placeholder="Yazım (örn. FTİH BASMACI)" className={`${inputCls} flex-1 min-w-[180px]`} data-testid="alias-raw" />
        <span className="text-slate-500 self-center">→</span>
        <input value={canon} onChange={e => setCanon(e.target.value)} placeholder="Asıl ad (örn. FATİH BASMACI)" className={`${inputCls} flex-1 min-w-[180px]`} data-testid="alias-canonical" />
        <button type="submit" className={`${btnCls} bg-blue-600 hover:bg-blue-500 text-white`} data-testid="alias-save-btn">Bağla</button>
        <button type="button" onClick={() => act(reviewsApi.rebuildAliases, "Otomatik eşleme yenilendi")} className={`${btnCls} bg-slate-700 hover:bg-slate-600 text-slate-200`} data-testid="alias-rebuild-btn"><RefreshCw className="w-4 h-4" /> Yenile</button>
      </form>
      <div className="max-h-[420px] overflow-y-auto space-y-2" data-testid="alias-groups">
        {loading && <div className="text-xs text-slate-500 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Yükleniyor…</div>}
        {!loading && groups.length === 0 && <div className="text-xs text-slate-500">Birleştirme yok.</div>}
        {groups.map(g => (
          <div key={g.canonical} className="bg-slate-800/60 border border-slate-700 rounded-lg px-3 py-2" data-testid={`alias-group-${g.canonical}`}>
            <div className="text-sm font-semibold text-emerald-300 flex items-center gap-2"><Users className="w-3.5 h-3.5" /> {g.canonical}</div>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {g.aliases.filter(a => a.raw !== g.canonical).map(a => (
                <span key={a.raw} className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${a.manual ? "border-blue-500/50 text-blue-200" : "border-slate-600 text-slate-300"}`} title={a.manual ? "Elle bağlandı" : "Otomatik"}>
                  {a.raw} <span className="text-slate-500">×{a.count}</span>
                  <button type="button" onClick={() => act(() => reviewsApi.setAlias(a.raw, ""), "Ayrıldı")} className="text-slate-500 hover:text-rose-300 ml-0.5" title="Ayır (kendi adıyla kalsın)" data-testid={`alias-detach-${a.raw}`}><Unlink className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}
